/**
 * Utility functions for generating map URLs
 */

/**
 * Detects if the current device is iOS
 */
export const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

/**
 * Generates a map URL for the given location
 * @param location - The location string (e.g., "56, Rangapillai Street, Puducherry")
 * @returns The appropriate map URL for the platform
 */
export const generateMapUrl = (location: string): string => {
  // Encode the location for URL
  const encodedLocation = encodeURIComponent(location);
  
  if (isIOS()) {
    // Apple Maps URL for iOS devices
    return `https://maps.apple.com/?q=${encodedLocation}`;
  } else {
    // Google Maps URL for other devices
    return `https://www.google.com/maps/search/?api=1&query=${encodedLocation}`;
  }
};

/**
 * Options for directions URL
 */
export interface MapDestinationOptions {
  address?: string;
  lat?: number;
  lng?: number;
}

/**
 * Generates a directions URL (navigation) for the given destination.
 * Prefers coordinates when available, falls back to address string.
 */
export const generateDirectionsUrl = (options: MapDestinationOptions): string => {
  const { address, lat, lng } = options;

  const hasCoords = typeof lat === 'number' && typeof lng === 'number';

  if (!hasCoords && !address) {
    return '';
  }

  if (isIOS()) {
    if (hasCoords) {
      // Apple Maps directions using coordinates
      return `https://maps.apple.com/?daddr=${lat},${lng}`;
    }
    // Apple Maps directions using address
    const encoded = encodeURIComponent(address as string);
    return `https://maps.apple.com/?daddr=${encoded}`;
  } else {
    if (hasCoords) {
      // Google Maps directions using coordinates
      const dest = encodeURIComponent(`${lat},${lng}`);
      return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
    }
    // Google Maps directions using address
    const encoded = encodeURIComponent(address as string);
    return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
  }
};

/**
 * Opens a location in maps in a new tab using directions (navigation) URL.
 * @param location - The location string
 */
export const openLocationInMaps = (location: string): void => {
  const mapUrl = generateDirectionsUrl({ address: location });
  if (!mapUrl) return;
  window.open(mapUrl, '_blank', 'noopener,noreferrer');
};
